import React, { Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box, Alert, Dialog, DialogTitle, DialogContent, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';

// Lazy load pages for code splitting
const HomePage = React.lazy(() => import('./pages/HomePage'));
const OpinionDetailPage = React.lazy(() => import('./pages/OpinionDetailPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const CreateOpinionPage = React.lazy(() => import('./pages/CreateOpinionPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const AdminTicketsPage = React.lazy(() => import('./pages/AdminTicketsPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const OrgAdminPage = React.lazy(() => import('./pages/OrgAdminPage'));
const MyTicketsPage = React.lazy(() => import('./pages/MyTicketsPage'));
const HowToUsePage = React.lazy(() => import('./pages/HowToUsePage'));
const TermsOfUsePage = React.lazy(() => import('./pages/TermsOfUsePage'));
const DisclaimerPage = React.lazy(() => import('./pages/DisclaimerPage'));

// Loading component for Suspense fallback
const LoadingFallback: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

type GuardProps = {
  minLevel?: number;
  orgOnly?: boolean;
  adminOnly?: boolean;
  children: React.ReactElement;
};

const GuardedRoute: React.FC<GuardProps> = ({ minLevel = 0, orgOnly = false, adminOnly = false, children }) => {
  const { identity, user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (minLevel > 0 && identity.authLevel < minLevel) {
    sessionStorage.setItem('lastPath', location.pathname);
    return <Navigate to="/login" replace />;
  }

  if (orgOnly && !identity.isOrgUser) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const SecurityStatusDialog: React.FC = () => {
  const { securityState, clearSecurityState } = useAuth();
  const open = securityState.code !== 'none';
  if (!open) return null;

  return (
    <Dialog open={open} onClose={clearSecurityState} maxWidth="xs" fullWidth>
      <DialogTitle>Security Notice</DialogTitle>
      <DialogContent>
        <Alert severity={securityState.code === 'rate_limited' ? 'warning' : 'error'} sx={{ mb: 2 }}>
          {securityState.message || 'Your session requires attention.'}
        </Alert>
        <Button variant="contained" fullWidth onClick={clearSecurityState}>
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const AppRoutes: React.FC = () => (
  <Suspense fallback={<LoadingFallback />}>
    <SecurityStatusDialog />
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/opinion/:id" element={<OpinionDetailPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/profile"
        element={
          <GuardedRoute minLevel={2}>
            <ProfilePage />
          </GuardedRoute>
        }
      />
      <Route path="/create-opinion" element={<CreateOpinionPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/admin/tickets"
        element={
          <GuardedRoute minLevel={4} adminOnly>
            <AdminTicketsPage />
          </GuardedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <GuardedRoute minLevel={4} adminOnly>
            <AdminDashboardPage />
          </GuardedRoute>
        }
      />
      <Route
        path="/org-admin"
        element={
          <GuardedRoute minLevel={5} orgOnly>
            <OrgAdminPage />
          </GuardedRoute>
        }
      />
      <Route
        path="/my-tickets"
        element={
          <GuardedRoute minLevel={2}>
            <MyTicketsPage />
          </GuardedRoute>
        }
      />
      <Route path="/how-to-use" element={<HowToUsePage />} />
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
    </Routes>
  </Suspense>
);

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const isFa = (i18n.language || 'fa').startsWith('fa');
  const fontFamily = isFa
    ? '"Vazir", "Tahoma", "Arial", sans-serif'
    : '"Roboto", "Helvetica", "Arial", sans-serif';

  const theme = useMemo(() => {
    let baseTheme = createTheme({
      palette: {
        mode: 'light',
        primary: { main: '#1976d2' },
        secondary: { main: '#dc004e' },
        info: { main: '#0288d1' },
        success: { main: '#2e7d32' },
        warning: { main: '#ed6c02' },
        background: {
          default: '#f3f6fa',
          paper: '#ffffff'
        },
        text: {
          primary: '#172033',
          secondary: '#5f6b7a'
        }
      },
      shape: {
        borderRadius: 12
      },
      typography: {
        fontFamily,
        h4: { fontWeight: 800, lineHeight: 1.25 },
        h5: { fontWeight: 700, lineHeight: 1.3 },
        h6: { fontWeight: 700, lineHeight: 1.35 },
        body1: { lineHeight: 1.75 },
        body2: { lineHeight: 1.65 }
      },
      direction: isFa ? 'rtl' : 'ltr',
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: {
              scrollBehavior: 'smooth'
            },
            body: {
              fontFamily,
              direction: isFa ? 'rtl' : 'ltr',
              background:
                'radial-gradient(circle at 10% -10%, rgba(25,118,210,0.08), rgba(243,246,250,1) 45%), #f3f6fa',
              minHeight: '100vh',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale'
            },
            '*': {
              boxSizing: 'border-box'
            },
            'input, textarea, select, button': {
              fontFamily
            },
            'a': {
              color: 'inherit'
            },
            '::selection': {
              background: 'rgba(25,118,210,0.25)'
            }
          }
        },
        MuiContainer: {
          styleOverrides: {
            root: {
              paddingLeft: '12px',
              paddingRight: '12px'
            }
          }
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              direction: isFa ? 'rtl' : 'ltr',
              borderRadius: 14
            }
          }
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 14,
              border: '1px solid rgba(15,23,42,0.06)',
              boxShadow: '0 8px 24px rgba(15,23,42,0.06)'
            }
          }
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(25,118,210,0.92)',
              backdropFilter: 'blur(8px)',
              borderBottom: '1px solid rgba(255,255,255,0.15)'
            }
          }
        },
        MuiTextField: {
          defaultProps: {
            size: 'medium',
            variant: 'outlined'
          }
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundColor: 'rgba(255,255,255,0.92)',
              transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${alpha('#1976d2', 0.16)}`
              },
              '& .MuiInputBase-input': {
                direction: isFa ? 'rtl' : 'ltr',
                textAlign: isFa ? 'right' : 'left'
              }
            }
          }
        },
        MuiInputLabel: {
          styleOverrides: {
            root: isFa
              ? {
                  right: 20,
                  left: 'auto',
                  transformOrigin: 'top right'
                }
              : {}
          }
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              textTransform: 'none',
              fontWeight: 700,
              minHeight: 42,
              paddingLeft: 16,
              paddingRight: 16,
              transition: 'transform 0.18s ease, box-shadow 0.18s ease'
            },
            contained: {
              boxShadow: '0 8px 18px rgba(25,118,210,0.18)'
            }
          }
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 10,
              fontWeight: 600
            }
          }
        },
        MuiAccordion: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              border: '1px solid rgba(15,23,42,0.08)',
              overflow: 'hidden',
              '&:before': {
                display: 'none'
              }
            }
          }
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: 16
            }
          }
        },
        MuiTableContainer: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              border: '1px solid rgba(15,23,42,0.08)'
            }
          }
        }
      }
    });

    baseTheme = responsiveFontSizes(baseTheme);
    return baseTheme;
  }, [fontFamily, isFa]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Header />
            <Box
              component="main"
              sx={{
                flex: 1,
                width: '100%',
                px: { xs: 1, sm: 2 },
                py: { xs: 1.5, md: 2.5 }
              }}
            >
              <AppRoutes />
            </Box>
            <Footer />
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
